import React from 'react';
import RydRollHeaderGroup from '../widgets/RydRollHeaderGroup';
import { TonalityHighlightToggle } from '../dialogs/TonalityHighlightToggle';
import NoteDurationSelector from '../pickers/DurationSelector';
import { RydViewport } from '../widgets/RydViewport';
import {
    getNewNoteDurationForInsert,
    getRenderedWidthForHNC,
    normalizeClassName
} from '../../render/renderUtils';
import {
    BarSet,
    ChordRollExpandedView,
    ToneRollKeybed
} from '../rolls/ChordRoll';

import { ChordContainer } from '../../model/containers';
import { RydCollapseControlButton } from '../widgets/RydCollapseControlButton';
import { ComputedContextFrame } from '../../model/context';
import { TrackNameDialog } from '../dialogs/TrackNameDialog';
import { RollHeaderPinnedRegion } from '../rolls/PianoRollInfoHeader';
import { CaretController } from '../../utils/ScrollController';
import { RydTrackUIState } from '../../model/tracks';
import { HarmonicPredicates } from '../../utils/harmonyHighlight';
import { ChordRollInputLayerCollpased } from '../rolls/ChordRollInputLayer';
import { useZoomFactor } from '../../stores/ZoomStore';
import { ChordCollapsed } from '../rolls/ChordCollapsed';
import { GridResolutionDialog } from '../pickers/GridResolutionDialog';
import {
    useSetWorkspaceUIState,
    useWorkspaceUIState
} from '../../stores/WorkspaceStore';
import { TrackHeader } from '../rolls/TrackHeader';

export function ChordRollTrack({
    trackId,
    contextFrame,
    caretController,
    hnc,
    setHnc,
    updateChordRollCB,
    harmonicPredicates,
    trackUiState,
    setTrackUIState,
}: {
    trackId: number,
    contextFrame: ComputedContextFrame,
    caretController?: CaretController,
    hnc: any,
    setHnc: (hnc: ChordContainer) => void,
    updateChordRollCB: (start: number, chord: any) => void,
    harmonicPredicates: HarmonicPredicates,
    trackUiState: RydTrackUIState,
    setTrackUIState?: (state: RydTrackUIState) => void,
}) {
    const zoomFactor = useZoomFactor();
    const ppq = hnc.getPPQ();
    const workspaceUiState = useWorkspaceUIState();
    const setWorkspaceUIState = useSetWorkspaceUIState();

    const header = <TrackHeader>
        <RydRollHeaderGroup>
            <TrackNameDialog
                label={"Harmony"}
                value={"Context: Harmony"}
                setValue={(v) => { }}
                readonly={true}
            />
            <RollHeaderPinnedRegion>
                <RydCollapseControlButton
                    collapsed={trackUiState.collapsed}
                    setCollapsed={
                        () => setTrackUIState?.({ ...trackUiState, collapsed: !trackUiState.collapsed })
                    } />
            </RollHeaderPinnedRegion>
        </RydRollHeaderGroup>
        <br />
        {!trackUiState.collapsed &&
            <>
                <RydRollHeaderGroup>
                    <TonalityHighlightToggle
                        contextFrame={contextFrame}
                        tonalityHighlightVisible={trackUiState.tonalityVisible}
                        setTonalityHighlightVisible={
                            (visible: boolean) => setTrackUIState?.({ ...trackUiState, tonalityVisible: visible })
                        } />
                </RydRollHeaderGroup>
                <RydRollHeaderGroup>
                    <NoteDurationSelector
                        updateSelectedDuratonCB={(duration) => setWorkspaceUIState({ ...workspaceUiState, newNoteDuration: duration })}
                        duration={workspaceUiState.newNoteDuration} />
                    <GridResolutionDialog
                        resolution={workspaceUiState.gridResolution}
                        setResolution={(r) => setWorkspaceUIState({ ...workspaceUiState, gridResolution: r })}
                        snap={workspaceUiState.snapToGrid}
                        setSnap={(s) => setWorkspaceUIState({ ...workspaceUiState, snapToGrid: s })}
                    />
                </RydRollHeaderGroup>
            </>
        }
    </TrackHeader>;

    const collapsedDisplayHack = trackUiState.collapsed ? {} : { display: 'inline-flex' };

    return (
        <RydViewport
            name="Chord Roll"
            hideTitle={true}
            sticky={header}
            adaptiveHeight={true}
            scrollController={caretController?.scrollController}>
            <div
                className="piano-roll-content-wrapper"
                style={{
                    ...collapsedDisplayHack,
                    width: getRenderedWidthForHNC({ notes: hnc, includeKeyboard: true })
                }} >

                {!trackUiState.collapsed &&
                    <ToneRollKeybed
                        tonality={contextFrame.tonality}
                    />}

                {/* Top part, collapsed */}
                <ChordRollInputLayerCollpased
                    hnc={hnc}
                    newNoteDuration={workspaceUiState.newNoteDuration}
                    updateChordRollCB={(start, chord) => {
                        console.log('updating from collapsed layer', chord);
                        updateChordRollCB(start, {
                            chord,
                            duration: hnc.get(start).length > 0
                                ? hnc.get(start)[0].duration
                                : getNewNoteDurationForInsert(workspaceUiState.newNoteDuration, hnc.getPPQ())
                        })
                    }}
                    gridResolution={workspaceUiState.gridResolution}
                    timeSignature={contextFrame.timeSignature}
                />
                {hnc.getGrid().map((ng, i) => ng.map((n, j) =>
                    <ChordCollapsed
                        key={i}
                        start={i}
                        updateChordCB={(chord) => updateChordRollCB(i, { chord: chord, duration: n.duration })}
                        updateChordDurationCB={(start, duration) => {
                            updateChordRollCB(start, { chord: hnc.get(start)[0].payload, duration: duration })
                        }}
                        chord={n}
                        ppq={ppq}
                        zoomFactor={zoomFactor}
                    />))}

                {/* Bottom part, expanded */}
                {!trackUiState.collapsed && <ChordRollExpandedView
                    trackId={trackId}
                    hnc={hnc}
                    timeSignature={contextFrame.timeSignature}
                    resolution={workspaceUiState.newNoteDuration}
                    updateChordRollCB={(start, chord) => updateChordRollCB(start, {
                        chord,
                        duration: hnc.get(start).length > 0
                            ? hnc.get(start)[0].duration
                            : workspaceUiState.newNoteDuration
                    })}
                    tonality={contextFrame.tonality}
                    updateHNC={setHnc}
                    harmonicPredicates={harmonicPredicates}
                />}

                <BarSet timeSignature={contextFrame.timeSignature} hnc={hnc} />
            </div>
        </RydViewport>
    );
}